// src/api/lenderPortal/performanceMetrics.js

import { api, extractMessage } from "../client";
import logger from "../../utils/logger";

/**
 * Helper to fetch a metric with 403-graceful handling
 */
async function fetchMetric(url, params = {}) {
  try {
    const response = await api.get(url, { params });
    return extractMessage(response?.data || response);
  } catch (error) {
    if (error.status === 403) {
      logger.warn(`Metric fetch 403 at ${url}: Permission denied or not whitelisted. Returning null.`);
      return null;
    }
    throw error;
  }
}

// 1) Lender approval rate
export async function getLenderApprovalRate(months = undefined) {
  const url = "/api/method/dynesourcerapi.api.get_lender_approval_rate";
  const params = months ? { months } : {};
  return fetchMetric(url, params);
}

// 2) Top performing products
export async function getTopPerformingProducts(months = undefined) {
  const url = "/api/method/dynesourcerapi.api.get_top_performing_products";
  const params = months ? { months } : {};
  return fetchMetric(url, params);
}

// 3) Average loan amount
export async function getAverageLoanAmount(months = undefined) {
  const url = "/api/method/dynesourcerapi.api.get_average_loan_rate"; // Note: backend uses _rate not _amount sometimes
  const params = months ? { months } : {};
  return fetchMetric(url, params);
}

// 4) Borrower satisfaction
export async function getBorrowerSatisfaction(months = undefined) {
  const url = "/api/method/dynesourcerapi.api.get_borrower_satisfaction";
  const params = months ? { months } : {};
  return fetchMetric(url, params);
}

/* ============================================
   Invoice Finance (if_analytics)
============================================ */
const IF_RANGE_BY_MONTHS = { 1: "1m", 3: "3m", 6: "6m" };

// Approval rate takes `range` (1m / 3m / 6m); the other IF metrics take `months`.
export async function getIfApprovalRate(months = undefined) {
  const url = "/api/method/dynesourcerapi.if_analytics.get_if_approval_rate";
  const range = IF_RANGE_BY_MONTHS[months];
  return fetchMetric(url, range ? { range } : {});
}

export async function getIfTopPerformingProducts(months = undefined) {
  const url = "/api/method/dynesourcerapi.if_analytics.get_if_top_performing_products";
  return fetchMetric(url, months ? { months } : {});
}

export async function getIfAverageLoanAmount(months = undefined) {
  const url = "/api/method/dynesourcerapi.if_analytics.get_if_average_loan_rate";
  return fetchMetric(url, months ? { months } : {});
}

export async function getIfBorrowerSatisfaction(months = undefined) {
  const url = "/api/method/dynesourcerapi.if_analytics.get_if_borrower_satisfaction";
  return fetchMetric(url, months ? { months } : {});
}

/* ============================================
   Convenience Functions
============================================ */
/**
 * @param {number} [months] 1 / 3 / 6
 * @param {string} [productType] "if" uses the Invoice Finance endpoints; anything else uses the default ones.
 */
export async function getAllPerformanceMetrics(months = undefined, productType = undefined) {
  const isIf = String(productType || "").toLowerCase() === "if";
  try {
    const [approvalRate, topPerformer, avgLoan, satisfaction] = await Promise.all(
      isIf
        ? [
            getIfApprovalRate(months),
            getIfTopPerformingProducts(months),
            getIfAverageLoanAmount(months),
            getIfBorrowerSatisfaction(months),
          ]
        : [
            getLenderApprovalRate(months),
            getTopPerformingProducts(months),
            getAverageLoanAmount(months),
            getBorrowerSatisfaction(months),
          ]
    );
    return { approvalRate, topPerformer, avgLoan, satisfaction };
  } catch (error) {
    logger.error("Error fetching all performance metrics:", error);
    throw error;
  }
}

export async function getAllPerformanceMetricsByPeriod() {
  const [month1, month3, month6] = await Promise.all([
    getAllPerformanceMetrics(1),
    getAllPerformanceMetrics(3),
    getAllPerformanceMetrics(6),
  ]);
  return { month1, month3, month6 };
}

export async function getMetricByPeriod(metricType) {
  let fetchFunction;
  switch (metricType) {
    case "approval": fetchFunction = getLenderApprovalRate; break;
    case "products": fetchFunction = getTopPerformingProducts; break;
    case "loan": fetchFunction = getAverageLoanAmount; break;
    case "satisfaction": fetchFunction = getBorrowerSatisfaction; break;
    default: throw new Error(`Invalid metric type: ${metricType}`);
  }

  const [month30, month90, month180] = await Promise.all([
    fetchFunction(1),
    fetchFunction(3),
    fetchFunction(6),
  ]);

  return {
    "30days": month30,
    "3months": month90,
    "6months": month180,
  };
}

const performanceMetrics = {
  getLenderApprovalRate,
  getTopPerformingProducts,
  getAverageLoanAmount,
  getBorrowerSatisfaction,
  getIfApprovalRate,
  getIfTopPerformingProducts,
  getIfAverageLoanAmount,
  getIfBorrowerSatisfaction,
  getAllPerformanceMetrics,
  getAllPerformanceMetricsByPeriod,
  getMetricByPeriod,
};

export default performanceMetrics;
